import { useState } from 'react';
import {
  Box, Paper, Typography, Button, ToggleButton, ToggleButtonGroup, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, Collapse,
} from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import BarChart from './BarChart';

const RANGES = [7, 30, 90];
const num = { fontVariantNumeric: 'tabular-nums' };
const fmt = (n) => (n === null || n === undefined ? '—' : n.toLocaleString());

function Stat({ label, value, hint }) {
  return (
    <Paper sx={{ px: 1.75, py: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.15rem', fontWeight: 600, lineHeight: 1.3 }} noWrap>{value}</Typography>
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', minHeight: '1.5em' }}>{hint}</Typography>
    </Paper>
  );
}

const sum = (days, key) => {
  const vals = days.map((d) => d[key]).filter((v) => v !== null);
  return vals.length ? vals.reduce((s, v) => s + v, 0) : null;
};

export default function ItchView({ data }) {
  const [range, setRange] = useState(30);
  const [showTable, setShowTable] = useState(false);
  const { game, history, snapshotHours } = data;
  const days = history.days.slice(-range);
  // The first tracked day is partial, so charts need at least one full day.
  const tracked = days.some((d) => d.views !== null && !d.partial);
  const conversion = game.views ? (game.downloads / game.views) * 100 : null;
  const earnings = game.earnings?.length ? game.earnings.map((e) => e.amount_formatted).join(' · ') : '—';
  const gain = (key) => {
    const v = sum(days, key);
    return !tracked || v === null ? 'collecting history…' : `+${fmt(v)} in ${range}d`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Game header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {game.coverUrl && (
          <Box component="img" src={game.coverUrl} alt="" sx={{ width: 64, height: 51, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
        )}
        <Box sx={{ minWidth: 0, flex: { xs: '1 1 calc(100% - 80px)', sm: 1 } }}>
          <Typography fontWeight={600} noWrap>{game.title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {game.published === false ? 'Draft' : game.publishedAt ? `Published ${new Date(game.publishedAt).toLocaleDateString()}` : 'Published'}
            {game.stale && ' · itch.io unreachable, showing last stored totals'}
          </Typography>
        </Box>
        <Button variant="outlined" color="inherit" size="small" startIcon={<InsightsOutlinedIcon />}
          href={`https://itch.io/game/summary/${game.id}`} target="_blank" rel="noopener"
          sx={{ borderColor: 'divider' }}
        >
          itch.io analytics
        </Button>
        {game.url && (
          <Button color="inherit" size="small" endIcon={<NorthEastIcon sx={{ fontSize: '14px !important' }} />}
            href={game.url} target="_blank" rel="noopener" sx={{ color: 'text.secondary' }}
          >
            Game page
          </Button>
        )}
      </Box>

      {/* Totals */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1.5 }}>
        <Stat label="Views" value={fmt(game.views)} hint={gain('views')} />
        <Stat label="Downloads" value={fmt(game.downloads)} hint={gain('downloads')} />
        <Stat label="Conversion" value={conversion === null ? '—' : `${conversion.toFixed(1)}%`} hint="downloads / views" />
        <Stat label="Purchases" value={fmt(game.purchases)} hint={gain('purchases')} />
        <Stat label="Earnings" value={earnings} hint="lifetime, before fees" />
      </Box>

      {/* Daily history */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>Daily</Typography>
        <ToggleButtonGroup size="small" exclusive value={range} onChange={(_, v) => v && setRange(v)}
          sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, fontSize: '0.75rem', textTransform: 'none' } }}
        >
          {RANGES.map((r) => <ToggleButton key={r} value={r}>{r} days</ToggleButton>)}
        </ToggleButtonGroup>
      </Box>

      {!tracked && (
        <Alert severity="info" variant="outlined">
          {history.trackingSince
            ? `Tracking started ${new Date(history.trackingSince).toLocaleString()}. `
            : 'No snapshots yet. '}
          itch.io only reports lifetime totals, so the server records them every {snapshotHours} h and
          daily numbers fill in from the next day. Older history is on the itch.io analytics page.
        </Alert>
      )}

      {tracked && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
          <BarChart title="Views per day" data={days.map((d) => ({ ...d, value: d.views }))} />
          <BarChart title="Downloads per day" data={days.map((d) => ({ ...d, value: d.downloads }))} />
        </Box>
      )}

      {tracked && <Box>
        <Button size="small" color="inherit" onClick={() => setShowTable((v) => !v)} sx={{ color: 'text.secondary' }}>
          {showTable ? 'Hide daily table' : 'Show daily table'}
        </Button>
        <Collapse in={showTable} unmountOnExit>
          <Paper sx={{ mt: 1, border: '1px solid', borderColor: 'divider', overflow: 'auto', maxHeight: 360 }}>
            <Table size="small" stickyHeader sx={{ '& td, & th': { py: 0.5, borderColor: 'divider' }, '& th': { bgcolor: 'background.paper', fontSize: '0.7rem', color: 'text.secondary' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Views</TableCell>
                  <TableCell align="right">Downloads</TableCell>
                  <TableCell align="right">Purchases</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...days].reverse().map((d) => (
                  <TableRow key={d.date}>
                    <TableCell sx={num}>{d.date}{d.partial && ' *'}</TableCell>
                    <TableCell align="right" sx={num}>{fmt(d.views)}</TableCell>
                    <TableCell align="right" sx={num}>{fmt(d.downloads)}</TableCell>
                    <TableCell align="right" sx={num}>{fmt(d.purchases)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Collapse>
      </Box>}
    </Box>
  );
}
